import {
  EuiButtonGroup,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPageTemplate,
  EuiSkeletonText,
  EuiSpacer,
  EuiTitle,
} from "@inscopix/ideas-eui";
import { ButtonCreateProject } from "../ButtonCreateProject/ButtonCreateProject";
import { Tenant } from "graphql/_Types";

import { RefObject, useMemo, useState } from "react";
import { CallOutError } from "components/CallOutError/CallOutError";
import { useGetProjects } from "hooks/useGetProjects";
import { ProjectCards } from "components/ProjectsCards/ProjectCards";
import { useIdeasSearchParams } from "hooks/useIdeasSearchParams";
import { useUserContext } from "providers/UserProvider/UserProvider";
import { ideasFeatures } from "ideas.features";

interface PanelProjectsProps {
  panelTitle?: string;
  tenantId?: Tenant["id"];
  showCreateProject?: boolean;
  virtuaScrollRef: RefObject<HTMLElement>;
}

export const PanelProjects = ({
  panelTitle = "Projects",
  tenantId,
  showCreateProject = true,
  virtuaScrollRef,
}: PanelProjectsProps) => {
  const hasPersonalSpaceOrTenant = useUserContext(
    (s) =>
      s.personalSpace !== undefined ||
      s.tenants.length > 0 ||
      s.externalTenants.length > 0,
  );

  const { projects, loading } = useGetProjects({ tenantId });
  const { getParam, setParam } = useIdeasSearchParams();
  const [view, setView] = useState<"GRID" | "CARDS">(() => {
    const viewParam = getParam("PROJECT_VIEW");
    if (viewParam === "grid") {
      return "GRID";
    }
    return "CARDS";
  });

  const projectsBody = useMemo(() => {
    if (loading) {
      return <EuiSkeletonText lines={5} />;
    }

    if (projects === undefined) {
      return <CallOutError />;
    }
    return (
      <ProjectCards
        projects={projects}
        tenantId={tenantId}
        view={view}
        virtuaScrollRef={virtuaScrollRef}
      />
    );
  }, [loading, projects, tenantId, view, virtuaScrollRef]);

  const viewOptions = [
    {
      id: "CARDS",
      label: "Cards",
      iconType: "apmTrace",
      value: "CARDS",
    } as const,
    {
      id: "GRID",
      label: "Grid",
      iconType: "tableDensityCompact",
      value: "GRID",
    } as const,
  ];

  // If there are no projects and no personal space or tenant to create a project in, just show a welcome message
  if (
    ideasFeatures.personalSpace &&
    !hasPersonalSpaceOrTenant &&
    projects?.length === 0
  ) {
    return (
      <EuiPageTemplate.Section
        grow={false}
        restrictWidth={false}
        style={{ minWidth: 500 }}
      >
        <EuiTitle>
          <h1>Welcome to IDEAS</h1>
        </EuiTitle>
      </EuiPageTemplate.Section>
    );
  }

  return (
    <EuiPageTemplate.Section
      grow
      restrictWidth={false}
      contentProps={{ style: { height: "100%" } }}
      style={{ minWidth: 500 }}
    >
      <EuiFlexGroup
        direction="column"
        gutterSize="none"
        style={{ height: "100%" }}
      >
        <EuiFlexGroup
          responsive={false}
          gutterSize="none"
          style={{ flexGrow: 0 }}
          alignItems="baseline"
        >
          <EuiFlexItem>
            <EuiTitle>
              <h4>{panelTitle}</h4>
            </EuiTitle>
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiButtonGroup
              type="single"
              legend="View"
              options={viewOptions}
              idSelected={view}
              onChange={(id) => {
                const paramKey =
                  (id as (typeof viewOptions)[number]["id"]) === "CARDS"
                    ? "cards"
                    : "grid";
                setParam("PROJECT_VIEW", paramKey);
                setView(id as (typeof viewOptions)[number]["id"]);
              }}
            />
          </EuiFlexItem>
          {showCreateProject && (
            <EuiFlexItem grow={false}>
              <ButtonCreateProject tenantId={tenantId} />
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
        <EuiSpacer size="s" />
        {projectsBody}
      </EuiFlexGroup>
    </EuiPageTemplate.Section>
  );
};
